import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	TensileRequirementByGrade,
	TensileRequirementVersion,
} from "../models/TensileRequirementByGrade";

export const getTensileRequirementVersions = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[LAB].[GetTensileRequirementVersions]",
		[],
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as TensileRequirementVersion[];
	} else {
		throw new Error(
			resp.message || "Error fetching tensile requirement versions",
		);
	}
};

export const getTensileRequirementsByGrade = async (
	params: { versionId?: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TensileRequirementVersionId", value: params.versionId ?? null },
	];

	const resp = await apiService.callV2(
		"[LAB].[GetTensileRequirementsByGrade]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as TensileRequirementByGrade[];
	} else {
		throw new Error(
			resp.message || "Error fetching tensile requirements by grade",
		);
	}
};

export const upsertTensileRequirements = async ({
	versionId,
	comments,
	rows,
}: {
	versionId: number | null;
	comments: string | null;
	rows: TensileRequirementByGrade[];
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TensileRequirementVersionId", value: versionId },
		{ name: "Comments", value: comments },
		{ name: "Rows", value: JSON.stringify(rows) },
	];

	const resp = await apiService.callV2(
		"[LAB].[UpsertTensileRequirements]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting tensile requirements");
	}
};

export const confirmTensileRequirementVersion = async (params: {
	versionId: number;
	rev: number;
	effectiveDate: Date;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TensileRequirementVersionId", value: params.versionId },
		{ name: "Rev", value: params.rev },
		{ name: "EffectiveDate", value: params.effectiveDate.toISOString() },
	];

	const resp = await apiService.callV2(
		"[LAB].[ConfirmTensileRequirementVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error confirming tensile requirement version",
		);
	}
};

export const unscheduleTensileRequirementVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TensileRequirementVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[UnscheduleTensileRequirementVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message ||
				"Error returning the tensile requirement version to Draft",
		);
	}
};

export const deleteTensileRequirementVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TensileRequirementVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[DeleteTensileRequirementVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error deleting tensile requirement version",
		);
	}
};
